/**
 * The connection to hg-realtime's staff stream: one per browser tab.
 *
 *   ticket (POST /v2/realtime/tickets) → EventSource (?ticket=) → events
 *
 * A browser's `EventSource` reconnects by itself, but with the URL it has,
 * whose ticket has run out by then. So this never lets it: on any error the
 * source is closed, a new ticket is asked for after a pause that grows, and
 * the stream is opened anew from the last place seen (`last_event_id`). What
 * was missed arrives first; when it cannot be sent again the service says
 * `resync`, which the screens answer by reading everything afresh.
 *
 * Nothing here knows about React or about queries: it takes its ticket, its
 * EventSource and its clock from outside, so that a test can stand in for
 * all three.
 */
import {
  ORDER_EVENT_TYPES,
  parseLiveEvent,
  placeOf,
  POSITION_EVENT_TYPE,
  STREAM_EVENT_TYPES,
  textOf,
  type LiveEvent,
} from "./events";

/**
 * - `idle`: not started (signed out, or a browser without `EventSource`).
 * - `connecting`: the first attempt.
 * - `connected`: the service said `connected`; events are live.
 * - `reconnecting`: the stream broke a moment ago and is being opened again.
 * - `down`: several attempts in a row have failed, or the service will not
 *   have this connection; the screens fall back to asking by themselves.
 */
export type LiveState = "idle" | "connecting" | "connected" | "reconnecting" | "down";

export interface LiveStatus {
  state: LiveState;
  /** When the last event arrived, as `now()`; null: none yet. */
  lastEventAt: number | null;
  /** Why the stream is down, when the service or the browser said: a problem's code, a `closing` reason. */
  reason: string | null;
}

/** As much of `EventSource` as the stream uses. */
export interface EventSourceLike {
  addEventListener: (type: string, listener: (event: MessageEventLike) => void) => void;
  close: () => void;
}
export interface MessageEventLike {
  data?: unknown;
  lastEventId?: string;
}

export interface LiveStreamDeps {
  /** A new ticket: asked for each time a connection is opened. */
  ticket: () => Promise<string>;
  /** Opens the stream at a URL. */
  open: (url: string) => EventSourceLike;
  /** The stream's address for a ticket, the last place seen, and whether points are wanted. */
  url: (params: { ticket: string; lastEventId: string | null; positions: boolean }) => string;
  now?: () => number;
  /** 0 to 1, for spreading the pauses; a test fixes it. */
  random?: () => number;
  /** The code of a failed ticket request, when the failure is a problem of the service's. */
  problemCode?: (error: unknown) => string | null;
}

/** The pause before attempt 2, 3, 4... of a broken stream, before spreading. */
export const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000] as const;
/** Failed attempts in a row after which the stream is said to be down. */
export const DOWN_AFTER = 3;
/** A stream that took longer than this to open the first time may have missed something. */
export const LATE_START_MS = 2_000;
/** How long a connection another tab took the place of waits before it asks again. */
export const SUPERSEDED_PAUSE_MS = 60_000;

/** Codes of a ticket refusal that no retry will change. */
const FINAL_CODES: ReadonlySet<string> = new Set(["forbidden"]);

/**
 * The events that have a place in hg-order's stream: only these are told
 * once and gone on from after a break. The rest (the service's own, a
 * desk's: a support message, a wallet, an adjustment) come with no `id:`,
 * and a browser stamps such a message with the last id it saw.
 */
const ORDER_TYPES: ReadonlySet<string> = new Set(ORDER_EVENT_TYPES);

type StatusListener = (status: LiveStatus) => void;
type EventListener = (event: LiveEvent) => void;

export class LiveStream {
  readonly #deps: Required<Omit<LiveStreamDeps, "problemCode">> &
    Pick<LiveStreamDeps, "problemCode">;
  #status: LiveStatus = { state: "idle", lastEventAt: null, reason: null };
  readonly #statusListeners = new Set<StatusListener>();
  readonly #eventListeners = new Set<EventListener>();

  #running = false;
  /** Bumped whenever a connection is given up: an answer for an older one is dropped. */
  #generation = 0;
  #source: EventSourceLike | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  /** Failed attempts in a row. */
  #failures = 0;
  #positions = false;
  /** The `id:` of the last event of hg-order's stream seen: where to go on from. */
  #lastEventId: string | null = null;
  #startedAt = 0;
  /** `connected` has been heard at least once since `start`. */
  #everConnected = false;
  /** The reason of a `closing` just heard, until the connection ends. */
  #closing: string | null = null;

  constructor(deps: LiveStreamDeps) {
    this.#deps = { now: () => Date.now(), random: () => Math.random(), ...deps };
  }

  status(): LiveStatus {
    return this.#status;
  }

  onStatus(listener: StatusListener): () => void {
    this.#statusListeners.add(listener);
    return () => {
      this.#statusListeners.delete(listener);
    };
  }

  onEvent(listener: EventListener): () => void {
    this.#eventListeners.add(listener);
    return () => {
      this.#eventListeners.delete(listener);
    };
  }

  /** Opens the stream and keeps it open until `stop`. */
  start(): void {
    if (this.#running) return;
    this.#running = true;
    this.#failures = 0;
    this.#everConnected = false;
    this.#lastEventId = null;
    this.#startedAt = this.#deps.now();
    this.#set({ state: "connecting", reason: null });
    void this.#connect();
  }

  /** Closes the stream; nothing is heard or tried until `start`. */
  stop(): void {
    if (!this.#running) return;
    this.#running = false;
    this.#drop();
    this.#set({ state: "idle", lastEventAt: null, reason: null });
  }

  /**
   * Whether the points of the specialists at work are wanted. The stream is
   * opened with or without them, so a change opens it anew, from the last
   * place seen: nothing is missed over it.
   */
  setPositions(wanted: boolean): void {
    if (this.#positions === wanted) return;
    this.#positions = wanted;
    if (!this.#running) return;
    this.#drop();
    void this.#connect();
  }

  /** Try now instead of waiting out the pause: the network is back, the tab is looked at again. */
  nudge(): void {
    if (!this.#running || this.#timer === null) return;
    clearTimeout(this.#timer);
    this.#timer = null;
    void this.#connect();
  }

  #set(change: Partial<LiveStatus>): void {
    this.#status = { ...this.#status, ...change };
    for (const listener of this.#statusListeners) listener(this.#status);
  }

  /** Gives up the connection in hand, and any wait for the next. */
  #drop(): void {
    this.#generation += 1;
    this.#source?.close();
    this.#source = null;
    this.#closing = null;
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }

  async #connect(): Promise<void> {
    const generation = ++this.#generation;
    let ticket: string;
    try {
      ticket = await this.#deps.ticket();
    } catch (error) {
      if (generation !== this.#generation || !this.#running) return;
      const code = this.#deps.problemCode?.(error) ?? null;
      if (code !== null && FINAL_CODES.has(code)) {
        // The service will not have this person on the stream: asking again changes nothing.
        this.#set({ state: "down", reason: code });
        return;
      }
      this.#failed(code);
      return;
    }
    if (generation !== this.#generation || !this.#running) return;

    const source = this.#deps.open(
      this.#deps.url({ ticket, lastEventId: this.#lastEventId, positions: this.#positions }),
    );
    this.#source = source;
    for (const type of STREAM_EVENT_TYPES) {
      source.addEventListener(type, (message) => {
        if (generation === this.#generation) this.#heard(type, message);
      });
    }
    source.addEventListener("error", () => {
      if (generation !== this.#generation) return;
      // Never the browser's own reconnect: its URL carries a ticket that has run out.
      source.close();
      this.#source = null;
      const closing = this.#closing;
      this.#closing = null;
      this.#failed(closing);
    });
  }

  #heard(type: string, message: MessageEventLike): void {
    const event = parseLiveEvent(type, message.data);
    if (event === null) return;

    if (type === POSITION_EVENT_TYPE) {
      // A point has no place in the stream: it is neither resumed from nor told twice.
      this.#set({ lastEventAt: this.#deps.now() });
      this.#emit(event);
      return;
    }

    if (type === "closing") {
      // The server ends the stream next; the browser then reports an error.
      this.#closing = textOf(event, "reason") ?? "closing";
      return;
    }

    const streamId = message.lastEventId ?? "";
    if (type === "connected") {
      // Its id is the place live events start after: a stream that breaks now goes on from there.
      if (streamId !== "") this.#lastEventId = streamId;
      const again = this.#everConnected;
      const late = !again && this.#deps.now() - this.#startedAt > LATE_START_MS;
      this.#everConnected = true;
      this.#failures = 0;
      this.#set({ state: "connected", reason: null, lastEventAt: this.#deps.now() });
      // A return says so: what of no area moved meanwhile is read again; the first is no return.
      this.#emit(again ? { ...event, reconnected: true } : event);
      // Opened long after the screens read their lists: what happened in between was not heard.
      if (late)
        this.#emit({ type: "resync", id: null, occurredAt: null, data: { reason: "late_start" } });
      return;
    }

    if (ORDER_TYPES.has(type) && streamId !== "") {
      // Another instance may send again what this page has seen: an event is told once.
      if (this.#seen(streamId)) return;
      this.#lastEventId = streamId;
    }
    // A desk's event has no place of its own: the id it carries is the last
    // one the browser saw, so it is neither checked against it nor kept.
    this.#set({ lastEventAt: this.#deps.now() });
    this.#emit(event);
  }

  /** The place of this id in the stream is not after the last one seen. */
  #seen(streamId: string): boolean {
    if (this.#lastEventId === null) return false;
    const last = placeOf(this.#lastEventId);
    const next = placeOf(streamId);
    if (last === null || next === null) return this.#lastEventId === streamId;
    return last.stream === next.stream && next.sequence <= last.sequence;
  }

  #emit(event: LiveEvent): void {
    for (const listener of this.#eventListeners) listener(event);
  }

  /** The connection could not be opened, or broke: wait, then open it again. */
  #failed(reason: string | null): void {
    if (!this.#running) return;
    if (reason === "superseded") {
      // One tab too many: asking again at once would only push another tab out.
      this.#set({ state: "down", reason });
      this.#wait(SUPERSEDED_PAUSE_MS);
      return;
    }
    this.#failures += 1;
    this.#set({
      state: this.#failures >= DOWN_AFTER ? "down" : "reconnecting",
      reason: this.#failures >= DOWN_AFTER ? reason : null,
    });
    const base = BACKOFF_MS[Math.min(this.#failures, BACKOFF_MS.length) - 1] ?? BACKOFF_MS[0];
    // Spread between three quarters and five quarters, so tabs do not come back in one moment.
    this.#wait(Math.round(base * (0.75 + this.#deps.random() * 0.5)));
  }

  #wait(ms: number): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#timer = null;
      void this.#connect();
    }, ms);
  }
}
